import { useEffect, useRef } from "react";
import ChartJS, { type ChartConfiguration } from "chart.js/auto";

import { Metrics, ChartData } from "~/components/analytics/shared/type";
import { chartOptions } from "~/components/analytics/shared/chartHelpers";

type ChartMetrics = Pick<Metrics, "chartType" | "metric">;

type Props = ChartMetrics & {
  height: string | number;
  width?: string | number;
  label?: string | null | undefined;
  options?: object | null | undefined;
  series: ChartData;
};

type ChartType = { destroy: () => void };

// Chart is a wrapper around Chart.js that fetches data from a given URL and renders a chart.
const Chart = ({ height, width, label, options, metric, chartType, series }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  let chart: ChartType;

  ChartJS.defaults.font.family =
    "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Helvetica, sans-serif";

  useEffect(() => {
    if (canvasRef.current) {
      const setChartOptions = chartOptions(
        chartType,
        series,
        metric,
        label,
        options,
      ) as ChartConfiguration;

      chart = new ChartJS(canvasRef.current, setChartOptions);
    }

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [series]);

  return (
    <div data-testid="chart-container" style={{ height: height }}>
      <canvas height={height} width={width} ref={canvasRef} data-testid="canvas" />
    </div>
  );
};

export default Chart;
